import { useCallback } from 'react'
import { useUserRole } from 'hooks/role/useUserRole'
import { useApplicationOrganization } from 'context/Organization'
import { useProfile } from 'context/Profile'
import { hasPermission, TYPES } from './helpers'
import { compileExpression } from 'filtrex'

import { log } from 'utils/logger'
import { useRouteType } from 'context/RouteType'

export function hasOrganizationContext({ isAuthenticated, isGlobalRole, isOrganizationContextRoute, isRead }) {
  // ignore organization context in Public mode
  if (!isAuthenticated) return false
  // organization users should always be in the context of an organization
  if (!isGlobalRole) return true
  // Feature: a global user should read in the context of an organization when navigating on organization or settings routes
  if (isGlobalRole && isOrganizationContextRoute && isRead) return true

  return false
}

function generatePermissionContext(resource, organization) {
  if (resource) {
    return resource.permissionsContext
  } else if (organization) {
    const { id, contracts: softwareContracts } = organization
    // Merge des defaultPermissions sur les contrats de type Software
    const contractPermissions = new Set()
    for (const { defaultPermissions } of softwareContracts) {
      defaultPermissions.forEach(contractPermissions.add, contractPermissions)
    }
    return [{ organization: { id }, permissions: [...contractPermissions] }]
  }
  return []
}

export function validateExpression({
  expression,
  userPermissions,
  resource,
  organization,
  isGlobalRole,
  isOrganizationContextRoute,
}) {
  const permissionsContext = generatePermissionContext(resource, organization)

  const options = {
    customProp: value => {
      try {
        if (value === 'globalRole') {
          return isGlobalRole
        }
        const organizationId = hasOrganizationContext({
          isRead: value.startsWith(TYPES.READ),
          isAuthenticated: true,
          isGlobalRole,
          isOrganizationContextRoute,
        })
          ? organization?.id
          : undefined

        return hasPermission(permissionsContext, value, userPermissions, organizationId)
      } catch (e) {
        log.error(e)
        return false
      }
    },
  }

  return !!compileExpression(expression, options)()
}

export function usePermissions() {
  const { isAuthenticated, isGlobalRole } = useProfile()
  const { organization } = useApplicationOrganization()
  const { role } = useUserRole()
  const { permissions: userPermissions } = role || {}
  const { isOrganizationContextRoute } = useRouteType()
  const matchPermissions = useCallback(
    (expression, resource) => {
      if (!expression) return true
      if (!isAuthenticated) throw new Error('Attempt to validate permission in public mode')
      return validateExpression({
        expression,
        userPermissions,
        resource,
        organization,
        isGlobalRole,
        isOrganizationContextRoute,
      })
    },
    [isAuthenticated, isGlobalRole, organization, userPermissions, isOrganizationContextRoute],
  )

  return { matchPermissions }
}
